import React from 'react';
import { Trophy, CheckCircle, ArrowRight, FileText, MessageCircle } from 'lucide-react';

interface SuccessScreenProps {
  phase: string;
  onContinueToNextPhase: () => void;
  onViewSummary: () => void;
  isFinalPhase: boolean;
  questionsAnswered: number;
  onStartNewInterview: () => void;
  onViewAllSummaries: () => void;
}

const SuccessScreen: React.FC<SuccessScreenProps> = ({
  phase,
  onContinueToNextPhase,
  onViewSummary,
  isFinalPhase,
  questionsAnswered,
  onStartNewInterview,
  onViewAllSummaries
}) => {
  const getPhaseDisplayName = (phase: string) => {
    switch (phase) {
      case 'phase1': return 'Phase 1';
      case 'phase2': return 'Phase 2'; 
      case 'project': return 'Project Phase';
      case 'final': return 'Final Phase';
      default: return phase;
    }
  };

  const getNextPhaseDisplayName = (phase: string) => {
    switch (phase) {
      case 'phase1': return 'Phase 2';
      case 'phase2': return 'Project Phase';
      case 'project': return 'Final Phase';
      default: return 'Next Phase';
    }
  };

  if (isFinalPhase) {
    return (
      <div className="bg-gradient-to-br from-purple-50 via-white to-pink-50 flex justify-center welcome-screen">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center max-w-8xl w-full">
          <div className="mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
              <Trophy className="w-8 h-8 text-white" />
            </div>
    
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">
              All Phases Complete!
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              You have successfully completed all interview phases. Your comprehensive 
              knowledge transfer documentation is now ready for review and will be 
              shared with your successor and team members.
            </p>
          </div>

          {/* Final Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <div className="text-3xl font-bold text-blue-600 mb-2">Complete</div>
              <div className="text-sm text-blue-800 font-medium">All Phases</div>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
              <div className="text-3xl font-bold text-green-600 mb-2">{questionsAnswered}</div>
              <div className="text-sm text-green-800 font-medium">Total Responses</div>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-pink-100 rounded-xl p-6 border border-purple-200">
              <div className="text-3xl font-bold" style={{ color: '#bb1ccc' }}>100%</div>
              <div className="text-sm font-medium" style={{ color: '#bb1ccc' }}>Progress Complete</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex  sm:flex-row gap-4 justify-center">
            {/* <button
              onClick={onViewSummary}
              style={{ background: '#bb1ccc' }}
              className="inline-flex items-center gap-3 px-8 py-4 text-white rounded-xl font-semibold hover:opacity-90 transition-all duration-200 transform hover:scale-105 shadow-md"
            >
              <FileText className="w-5 h-5" />
              View Final Summary
            </button> */}
            <button
              onClick={() => {
                onViewAllSummaries()
                onViewSummary()
              }}
              className="inline-flex items-center gap-3 px-8 py-3 border-2 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-all duration-200 transform hover:scale-105"
              style={{ borderColor: '#bb1ccc', color: '#bb1ccc' }}
            >
              <FileText className="w-5 h-5" />
              Review All Phase Summaries
            </button>
            <button
              onClick={onStartNewInterview}
              className="inline-flex items-center gap-3 px-8 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-all duration-200 transform hover:scale-105"
            >
              Start New Interview
            </button>
          </div>

          {/* <div className="flex justify-center">
            <button
              onClick={onStartNewInterview}
              className="inline-flex items-center gap-3 px-8 py-4 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-all duration-200 transform hover:scale-105"
            >
              Start New Interview
            </button>
          </div> */}

          {/* Next Steps */}
          <div className="mt-10 bg-gray-50 rounded-xl p-6 text-left">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-left">What happens next?</h3>
            <div className="space-y-3">
            <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Draft of KT will be send to Manager for  review</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Final documentation will be generated and reviewed</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Your successor will receive comprehensive handover materials</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Team members will have access to all relevant knowledge</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-green-50 via-white to-blue-50 flex justify-center welcome-screen">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-10 text-center w-full">
        <div className="mb-8">
          <div className="w-24 h-24 bg-gradient-to-r from-purple-200 to-purple-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle className="w-12 h-12 text-purple-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Response Submitted Successfully!
          </h1>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            Your {getPhaseDisplayName(phase)} responses have been successfully submitted 
            and processed. You're ready to move on to the next phase of the interview.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium text-green-700">
                {getPhaseDisplayName(phase)} Complete
              </span>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 border-2 border-purple-300 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-purple-600">!</span>
              </div>
              <span className="text-sm font-medium text-purple-600">
                {getNextPhaseDisplayName(phase)} Ready
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="text-2xl font-bold text-green-600 mb-1">{questionsAnswered}</div>
            <div className="text-xs text-green-800 font-medium">Questions Answered</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="text-2xl font-bold text-blue-600 mb-1">100%</div>
            <div className="text-xs text-blue-800 font-medium">Phase Complete</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex  gap-4 justify-center ">
          <button
            onClick={onContinueToNextPhase}
            style={{ background: '#bb1ccc' }}
            className="inline-flex items-center justify-center gap-3 px-8 py-4 text-white rounded-xl font-semibold hover:opacity-90 transition-all duration-200 transform hover:scale-105 shadow-md"
          >
            Continue to {getNextPhaseDisplayName(phase)}
            <ArrowRight className="w-5 h-5" />
          </button>
          
          {/* <button
            onClick={() => {
              onViewAllSummaries()
              onViewSummary()
            }}
            className="inline-flex items-center justify-center gap-3 px-8 py-3 border-2 border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200"
          >
            <FileText className="w-4 h-4" />
            Review Phase Summary
          </button> */}
        </div>

        {/* Helpful Note */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm text-blue-800">
            💡 You can review your previous responses at any time during the next phase.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuccessScreen;